const CACHE_NAME = "skp-card-background-v1";
const CACHE_KEY = "/skp-card-background/flexigeld";

const hasCacheStorage = () => typeof window !== "undefined" && "caches" in window;

const isValidPayload = (payload) => payload && typeof payload.content === "string" && payload.content.length > 0;

const toHex = (buffer) => Array.from(new Uint8Array(buffer))
  .map((value) => value.toString(16).padStart(2, "0"))
  .join("");

const getDigest = async (bytes) => {
  if (!window.crypto || !window.crypto.subtle) {
    return null;
  }

  return toHex(await window.crypto.subtle.digest("SHA-256", bytes));
};

const fromBase64 = (content) => {
  const binary = window.atob(content.replace(/\s/g, ""));
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

export const getCachedCardBackground = async () => {
  if (!hasCacheStorage()) {
    return null;
  }

  try {
    const cache = await window.caches.open(CACHE_NAME);
    const response = await cache.match(CACHE_KEY);
    if (!response) {
      return null;
    }

    const payload = await response.json();
    return isValidPayload(payload) ? payload : null;
  } catch (error) {
    console.warn("Unable to read the card background cache", error);
    return null;
  }
};

export const cacheCardBackground = async (payload) => {
  if (!hasCacheStorage() || !isValidPayload(payload)) {
    return;
  }

  try {
    const cache = await window.caches.open(CACHE_NAME);
    await cache.put(CACHE_KEY, new Response(JSON.stringify(payload), {
      headers: {"Content-Type": "application/json"},
    }));
  } catch (error) {
    console.warn("Unable to save the card background cache", error);
  }
};

export const toCardBackgroundDataUrl = (content) => `data:image/png;base64,${content}`;

export const matchesFallbackBackground = async (content, fallbackUrl) => {
  try {
    const fallbackResponse = await fetch(fallbackUrl);
    if (!fallbackResponse.ok) {
      return null;
    }

    const [contentDigest, fallbackDigest] = await Promise.all([
      getDigest(fromBase64(content)),
      fallbackResponse.arrayBuffer().then((buffer) => getDigest(buffer)),
    ]);

    return contentDigest === null || fallbackDigest === null ? null : contentDigest === fallbackDigest;
  } catch (error) {
    console.warn("Unable to compare the card background with the fallback asset", error);
    return null;
  }
};
